import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ClipboardCheck, Lock, Bell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { inr, num } from "@/lib/format";
import { Loading, StatusBadge } from "@/components/common/kit";

function Card({ title, children, testid }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-4 fade-up" data-testid={testid}>
      <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500">{title}</p>
      {children}
    </div>
  );
}

function Row({ label, value, strong, testid }) {
  return (
    <div className={`flex justify-between py-1 ${strong ? "border-t border-slate-100 pt-2 text-base font-bold" : "text-sm"}`}>
      <span className="text-slate-600">{label}</span><span className="font-mono tabular" data-testid={testid}>{value}</span>
    </div>
  );
}

export default function Today() {
  const { salesman, bizDate } = useAuth();
  const { data: d } = useFetch(`/day/${salesman.id}/${bizDate}`);
  if (!d) return <Loading />;
  const t = d.totals;
  const closed = d.status === "CLOSED";
  const pending = d.validation?.pending_transfers || d.validation?.pending_returns;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Today</h1>
        <StatusBadge status={d.status} testid="today-status" />
      </div>
      {pending > 0 && (
        <div className="flex items-center gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="today-pending-alert">
          <Bell className="h-4 w-4" /> {d.validation.pending_transfers} transfer(s) and {d.validation.pending_returns} return(s) awaiting admin approval
        </div>
      )}
      <Card title="Today's Stock (packs)" testid="today-stock-card">
        <Row label="Opening Stock" value={num(t.opening)} testid="today-opening" />
        <Row label="+ Stock Issued Today" value={num(t.issued)} testid="today-issued" />
        <Row label="+ Transfer In" value={num(t.transfer_in)} testid="today-transfer-in" />
        <Row label="− Transfer Out" value={num(t.transfer_out)} testid="today-transfer-out" />
        {(t.returned > 0 || t.loss !== 0) && <Row label="− Returns / Loss" value={`${num(t.returned)} / ${num(t.loss)}`} />}
        <Row label="Current Expected Stock" value={num(t.available)} strong testid="today-available" />
      </Card>
      <div className="grid grid-cols-2 gap-3">
        <Card title="Packs Sold"><p className="font-mono text-2xl font-bold" data-testid="today-packs-sold">{num(t.packs_sold)}</p></Card>
        <Card title="Sales Value"><p className="font-mono text-2xl font-bold" data-testid="today-sales-value">{inr(t.sales_value)}</p></Card>
      </div>
      <Card title="Collection" testid="today-collection-card">
        <Row label="Cash" value={inr(d.cash)} testid="today-cash" />
        <Row label="GPay / UPI" value={inr(d.gpay)} testid="today-gpay" />
        <Row label="Expenses" value={inr(d.expenses_total)} testid="today-expenses" />
        <Row label={d.surplus > 0 ? "Cash Surplus" : "Salesman Receivable"} value={inr(d.surplus > 0 ? d.surplus : d.receivable)} strong testid="today-receivable" />
      </Card>
      {closed ? (
        <div className="flex items-center gap-2 rounded-md bg-emerald-50 p-4 text-sm font-semibold text-emerald-800"><Lock className="h-4 w-4" /> Day submitted & locked</div>
      ) : (
        <Link to="/s/close"><Button className="h-14 w-full bg-blue-600 text-base font-bold hover:bg-blue-700" data-testid="today-close-day-button"><ClipboardCheck className="mr-2 h-5 w-5" /> Close Day</Button></Link>
      )}
    </div>
  );
}
